import {clearLocalStorage, useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import omitBy from 'lodash-es/omitBy'
import {useCallback, useMemo} from 'react'

import {useViews} from '../../hooks/use-views'
import {useProjectNumber} from '../../state-providers/memex/use-project-number'
import {ExpandedRowsContext} from './use-expanded-rows'

type ProjectExpandedRows = Record<number, ExpandedRowsByView>
type ExpandedRowsByView = Record<number, Array<string>>

export const EXPANDED_ROWS_STORAGE_KEY = 'projects.expandedRows'
const EMPTY_PROJECT_ROWS: ProjectExpandedRows = {}

/** Removes all persisted expanded-rows state. Intended for test cleanup only. */
export function clearExpandedRowsForTests(): void {
  clearLocalStorage([EXPANDED_ROWS_STORAGE_KEY])
}

export function ExpandedRowsProvider({children}: {children?: React.ReactNode}) {
  const {projectNumber} = useProjectNumber()

  const [allData, setAllData] = useLocalStorage<ProjectExpandedRows>(EXPANDED_ROWS_STORAGE_KEY, EMPTY_PROJECT_ROWS)

  // rowMap is the current project's per-view expanded row IDs
  const rowMap = useMemo(() => allData[projectNumber] ?? {}, [allData, projectNumber])

  const setRowMap = useCallback(
    (updater: ExpandedRowsByView | ((prev: ExpandedRowsByView) => ExpandedRowsByView)) => {
      setAllData(prevAllData => {
        const prevRowMap = prevAllData[projectNumber] ?? {}
        const newRowMap = typeof updater === 'function' ? updater(prevRowMap) : updater
        const pruned = omitBy(newRowMap, rows => !Array.isArray(rows) || rows.length === 0) as ExpandedRowsByView
        if (Object.keys(pruned).length === 0) {
          const updated = omitBy(prevAllData, (_, k) => Number(k) === projectNumber) as ProjectExpandedRows
          // instead of writing an empty object, return undefined to remove the key entirely for better hygiene
          return (Object.keys(updated).length === 0 ? undefined : updated) as ProjectExpandedRows
        }
        return {...prevAllData, [projectNumber]: pruned}
      })
    },
    [projectNumber, setAllData],
  )

  const {currentView} = useViews()
  const currentViewNumber = currentView?.number

  const toggleRowExpanded = useCallback(
    (rowId: string) => {
      if (!currentViewNumber) return
      setRowMap(prevMap => {
        const prevExpandedRows = prevMap[currentViewNumber] || []
        if (prevExpandedRows.includes(rowId)) {
          return {
            ...prevMap,
            [currentViewNumber]: prevExpandedRows.filter(id => id !== rowId),
          }
        }
        return {
          ...prevMap,
          [currentViewNumber]: [...prevExpandedRows, rowId],
        }
      })
    },
    [currentViewNumber, setRowMap],
  )

  const getExpandedRowsForView = useCallback(
    (viewNumber: number) => {
      return rowMap[viewNumber] || []
    },
    [rowMap],
  )

  const expandedRows = useMemo(() => {
    if (!currentViewNumber) return []
    return getExpandedRowsForView(currentViewNumber)
  }, [currentViewNumber, getExpandedRowsForView])

  const isRowExpandedForView = useCallback(
    (viewNumber: number, rowId: string) => {
      const expandedRowsForView = getExpandedRowsForView(viewNumber)
      return expandedRowsForView.includes(rowId)
    },
    [getExpandedRowsForView],
  )

  const isRowExpanded = useCallback(
    (rowId: string) => {
      if (!currentView) return false
      return isRowExpandedForView(currentView.number, rowId)
    },
    [currentView, isRowExpandedForView],
  )

  return (
    <ExpandedRowsContext
      value={useMemo(
        () => ({
          expandedRows,
          toggleRowExpanded,
          getExpandedRowsForView,
          isRowExpanded,
          isRowExpandedForView,
        }),
        [expandedRows, getExpandedRowsForView, isRowExpanded, isRowExpandedForView, toggleRowExpanded],
      )}
    >
      {children}
    </ExpandedRowsContext>
  )
}
